import { isEmpty } from 'lodash-es'
import { PropType, computed, defineComponent, reactive, ref } from 'vue'

import {
    CsLinearPagination,
    CsLoadingSpinner,
    CsPrimaryButton,
    M_Resultlist_Disturber_Container as MsDisturber,
    MsHeadline,
} from '@sunrise/components'
import { MsBell } from '@sunrise/icons'
import { storeToRefs } from '@sunrise/pinia'
import { useGlobalStore } from '@sunrise/shared'
import { Components } from '@sunrise/types'

import { ResultListData } from '../../cmsElements/M/M_RealEstate_SearchList/M_RealEstate_SearchList.interface'
import { DecoratedEstate } from '../../ts/mapper/decoratedItemEstate.interface'
import { CmsElementProperty } from '../../ts/shared/enum/cms.enum'
import { useMetaStore } from '../../ts/store/metaStoreModule/metaStoreModule'
import { useResultListStore } from '../../ts/store/resultListStoreModule/resultListStoreModule'

import MsImmoweltBadge from '../../static/illus/immoweltBadge.svg?component'
import MsMailAgentBanner from '../MsMailAgentBanner/MsMailAgentBanner.vue'
import { MailAgentTrackingVariant } from '../MsMailAgentModal/MsMailAgentModal.enum'
import MsMailAgentModal from '../MsMailAgentModal/MsMailAgentModal.vue'
import MsResultListItem from '../MsResultListItem/MsResultListItem.vue'
import MsSafetyInstructions from '../MsSafetyInstructions/MsSafetyInstructions.vue'
import { SearchBoxDefault } from '../MsSearchBox/MsSearchBox.enum'
import { AdSlotDisturber, ResultListState } from './MsResultList.interface'
import MsEmptyResultListSvg from './illus/empty-resultlist.svg?component'

export default defineComponent({
    name: 'MsResultList',
    components: {
        CsLinearPagination,
        CsLoadingSpinner,
        CsPrimaryButton,
        MsMailAgentBanner,
        MsMailAgentModal,
        MsResultListItem,
        MsDisturber,
        MsImmoweltBadge,
        MsEmptyResultListSvg,
        MsHeadline,
        MsBell,
        MsSafetyInstructions,
    },
    props: {
        data: { type: Object as PropType<ResultListData>, required: true },
        items: { type: Object as PropType<DecoratedEstate[]>, required: true },
        isLoading: { type: Boolean, default: false },
        privacyPoliceUrl: String,
        totalCount: { type: Number, required: true },
    },
    emits: ['scrollToTop'],
    setup(props, { emit }) {
        const elList = ref<HTMLDivElement | null>(null)
        const elFirstItem = ref<HTMLDivElement | null>(null)
        const resultListStore = useResultListStore()

        const requestContext = useGlobalStore().requestContext
        /* c8 ignore next */
        const currentUrl: string = requestContext?.url.href || ''

        const { privacyManagerId } = storeToRefs(useMetaStore())

        privacyManagerId.value = useGlobalStore().privacyManagerId || ''

        const currentPage = computed(() => resultListStore.searchParams.page || SearchBoxDefault.PAGE)
        const pageSize = computed(() => resultListStore.searchParams.pageSize || SearchBoxDefault.PAGE_SIZE)
        const showPagination = computed(() => props.totalCount > pageSize.value)
        const totalPages = computed(() => Math.ceil(props.totalCount / pageSize.value))
        const modalIsVisible = ref<boolean>(false)

        const resultListState: ResultListState = reactive({
            currentPage: currentPage,
            isLoading: false,
            pageSize: pageSize,
            totalPages: totalPages,
            showPagination: showPagination,
        })

        const updatePageInStore = async (page: number): Promise<void> => {
            resultListStore.searchParams.page = page
            emit('scrollToTop')
        }

        const hasMailAgentForIndex = (index: number): boolean => props.data.disturber?.mailAgent?.index === index

        const adSlotDisturber: AdSlotDisturber = {
            foundAdSlots: [],

            has: function (index: number) {
                const resultListPosition: number = index + 1
                this.foundAdSlots =
                    props.data.disturber?.adSlot?.filter((item): boolean => {
                        return item.properties[CmsElementProperty.disturberPosition] === resultListPosition
                    }) ?? []
                return !isEmpty(this.foundAdSlots)
            },
            get: function () {
                return this.foundAdSlots
            },
        }

        const isFirstPartnerItem = (currentIndex: number, isCurrentItemOwnInventory: boolean): boolean =>
            !isCurrentItemOwnInventory && (currentIndex === 0 || isPrevItemOwnInventory(currentIndex))

        const isPrevItemOwnInventory = (currentIndex: number): boolean => props.items[currentIndex - 1].ownInventory

        const hasItems = (): boolean => props.totalCount != 0

        function openModal() {
            modalIsVisible.value = true
        }

        function closeModal() {
            modalIsVisible.value = false
        }

        return {
            currentUrl,
            elList,
            elFirstItem,
            privacyManagerId,
            resultListState,
            adSlotDisturber,
            hasMailAgentForIndex,
            updatePageInStore,
            isFirstPartnerItem,
            hasItems,
            MailAgentTrackingVariant,
            infoState: Components.MsNotification.NotificationState.INFO,
            openModal,
            closeModal,
            modalIsVisible,
        }
    },
})
